/**
 * Every visible string of Settings › Billing (#1037), and the one pure
 * function that turns `GET /api/billing/status` into the state to draw.
 *
 * The words are `SettingsBilling.dc.html` (design-round-7/payments, sha256
 * 14bdcbd9…108b), D192, D196.4, D198.2-3, D200.1, D423/D424. A price is never
 * written here and the card never shows one: the status carries no amount, and
 * a list price read from `/billing/plans` could differ from the price the
 * subscription was made at. The talking-allowance line is not drawn either
 * (D413, D424: the pay screens say nothing about talking).
 */
import { formatRenewal } from "./payCopy";

export const BILLING_TITLE = "Billing";
export const BILLING_DESC = "Your plan, the card that pays for it, and how to stop.";

// ---- the closed fold's chip ----
export const CHIP_MONTHLY = "Monthly";
export const CHIP_ANNUAL = "Annual";
export const CHIP_STOPPED = "Stopped";
export const CHIP_NO_PLAN = "No plan";
/** While loading and when the read failed: an em dash, never a guess. */
export const CHIP_UNKNOWN = "—";

// ---- the plan panel ----
export const PLAN_EYEBROW = "Your plan";
export const BILLING_PLAN_NAME = { player: "Player", coach: "Coach", both: "Player and coach" };
export const PERIOD_LABEL = { monthly: "Monthly", annual: "Annual · 10% off" };
export const NEXT_CHARGE = "Next charge";
export const RENEWS = "Renews";
export const STRIPE_HOLDS_LINE = "Stripe holds your card. Nothing about it is stored in this app, so there is nothing about it to show you.";
export const APP_KEEPS_LINE =
  "This app keeps no payment records of its own: it reads your plan, when it renews and whether the last charge went through from Stripe. Stripe emails your receipts.";
export const NO_REFUND_ANNUAL_LINE =
  "If you stop part-way through the year there is no refund, and you keep access to the end of the year you paid for.";

// ---- Update card ----
export const UPDATE_CARD = "Update card";
export const UPDATE_CARD_BUSY = "Opening Stripe";
export const UPDATE_CARD_NOTE = "Opens Stripe's own page. You come back here when it is done.";
export const UPDATE_CARD_FAILED = "Stripe's page did not open. Nothing has changed. Try again in a moment.";

// ---- Stop billing ----
export const STOP_BILLING = "Stop billing";
export const STOP_CONFIRM = "Yes, stop billing";
export const STOP_CANCEL = "Cancel";
export const STOPPING = "Stopping…";
export const NOT_DELETING = "This is not deleting your account. Stopping billing keeps the account and everything in it.";
/** True whatever happened (review of #1592, F3): the client cannot tell a failed stop from a partial one. */
export const STOP_FAILED = "We couldn't confirm that billing stopped. Trying again is safe and charges nothing.";
export const stopExplain = (endText) =>
  endText
    ? `Stops the next charge. Your access runs to the end of the period you have paid for, ${endText}, and your record is kept. Nothing further is charged.`
    : "Stops the next charge. Your access runs to the end of the period you have paid for, and your record is kept. Nothing further is charged.";
export const stopArmed = (endText) =>
  endText
    ? `Stop billing? Nothing further is charged. You keep access until ${endText}, and your record is kept.`
    : "Stop billing? Nothing further is charged. You keep access to the end of the period you have paid for, and your record is kept.";

// ---- stopped ----
/** What stopping did and did not do. It claims no access: "Your plan runs to" says that, while the period lasts. */
export const STOPPED_LINE = "Billing is stopped. Nothing further is charged, and your record is kept.";
/** D425: shown only while the paid period's end is in the future. */
export const planRunsTo = (endText) => `Your plan runs to ${endText}`;
/** D425: opens Stripe's billing portal, where the cancel can be undone. */
export const KEEP_MY_PLAN = "Keep my plan";

// ---- no plan ----
export const NO_PLAN_TITLE = "You don't have a plan yet";
export const NO_PLAN_BODY = "Your account and everything in it are saved. There is no trial: the plan starts when the payment does.";
export const CHOOSE_A_PLAN = "Choose a plan";

// ---- renewal failed (D196.4) ----
export const RENEWAL_FAILED =
  "Your last payment didn't go through. You still have access while Stripe retries the card. Update your card to keep your plan running.";
/** An 11-to-17 player cannot update the card: the sentence that asked them to is left out. */
export const RENEWAL_FAILED_GUARDIAN = "Your last payment didn't go through. You still have access while Stripe retries the card.";

// ---- 11 to 17 (D192.2) ----
export const GUARDIAN_TITLE = "Paid by a parent or guardian";
export const guardianLine = (masked) =>
  masked
    ? `${masked} pays for this plan. The card and Stop billing are theirs, so they are not on your account.`
    : "A parent or guardian pays for this plan. The card and Stop billing are theirs, so they are not on your account.";

// ---- load ----
export const BILLING_FAILED_WHAT = "your billing";

/** F6 (review of #1592): a row with no period says no period word, never a guessed "Monthly". */
const periodChip = (period) => (period === "annual" ? CHIP_ANNUAL : period === "monthly" ? CHIP_MONTHLY : CHIP_UNKNOWN);

/**
 * What to draw, from the status body and who is looking.
 *
 * `kind` is one of: `no-plan` (none or incomplete: nothing is paid yet),
 * `running` (active), `renewal-failed` (past_due: access continues while
 * Stripe retries, D196.4), `stopped`. `guardianPays` is the 11-to-17 case.
 * `chip` is the closed fold's word. `accessLeft` is true only while the paid
 * period's end is in the future: a stopped line never claims access beyond it.
 */
export function billingView(status, { guardianPays = false, now = new Date() } = {}) {
  const s = status && typeof status === "object" ? status : {};
  const period = s.period === "annual" ? "annual" : s.period === "monthly" ? "monthly" : null;
  const endText = formatRenewal(s.current_period_end);
  const endMs = typeof s.current_period_end === "string" ? Date.parse(s.current_period_end) : NaN;
  const accessLeft = Number.isFinite(endMs) && endMs > now.getTime();
  const base = {
    planName: BILLING_PLAN_NAME[s.plan_code] || null,
    period,
    periodLabel: period ? PERIOD_LABEL[period] : null,
    endText,
    accessLeft,
    guardianPays,
    masked: typeof s.payer_email_masked === "string" && s.payer_email_masked ? s.payer_email_masked : null,
  };
  switch (s.status) {
    case "active":
      return { ...base, kind: "running", chip: periodChip(period) };
    case "past_due":
      return { ...base, kind: "renewal-failed", chip: periodChip(period) };
    case "stopped":
      return { ...base, kind: "stopped", chip: CHIP_STOPPED };
    default:
      return { ...base, kind: "no-plan", chip: CHIP_NO_PLAN };
  }
}
